import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { subcontractRequestsApi, type SubcontractRequestFilter } from '@/lib/apiClient';
import { Button, StateView } from '@/components';
import SubcontractRequestList from './SubcontractRequestList';
import { matchesRequest } from './contractsModel';

const FILTERS: readonly { key: SubcontractRequestFilter; label: string; title: string; empty: string }[] = [
  { key: 'waiting', label: 'Waiting for a number', title: 'Subcontract requests waiting for a number', empty: 'No requests are waiting. When a project manager submits one it appears here.' },
  { key: 'numbered', label: 'Numbered', title: 'Subcontract requests with a number', empty: 'No submitted request has a number yet.' },
  { key: 'all', label: 'All submitted', title: 'All submitted subcontract requests', empty: 'Nothing has been submitted yet.' },
];

/** How many requests one fetch brings. The API's default; a page shorter than this is the last. */
export const QUEUE_PAGE = 200;

/**
 * The coordinator's queue (#627): requests a project manager has submitted, oldest first, on the jobs she can see.
 * She opens one, creates the subcontract in Intacct, types its number onto the request and downloads the Word file.
 *
 * "Waiting" is the work: no number yet, and a row still on the Contracts page that has not been voided. A request
 * whose row was voided or removed is no longer waiting on anyone, so it is only under "All submitted". The list
 * carries no dollar amount: that is in the request itself.
 *
 * ⚠️ THE QUEUE IS PAGED. The API answers a page at a time and orders oldest first, so past one page a plain fetch
 * would hide the NEWEST requests, and under "Numbered" and "All submitted" (which only grow) that is where the live
 * work is. **Show more** brings the next page; the search box narrows what is loaded and says so when more is waiting.
 *
 * `search` is the Contracts page's own search box, so one box narrows the whole page: the queue as well as the board.
 */
export default function SubcontractRequestQueue({ search }: { search: string }) {
  const [filter, setFilter] = useState<SubcontractRequestFilter>('waiting');
  const current = FILTERS.find((f) => f.key === filter) ?? FILTERS[0];
  const q = useInfiniteQuery({
    queryKey: ['contracts', 'requests', 'queue', filter],
    queryFn: ({ pageParam }) => subcontractRequestsApi.queue(filter, { limit: QUEUE_PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.length < QUEUE_PAGE ? undefined : all.length * QUEUE_PAGE),
    staleTime: 30_000,
  });
  // StateView reads a plain list; the pages are joined into one.
  const view = { isPending: q.isPending, isError: q.isError, error: q.error, data: q.data?.pages.flat() };

  return (
    <>
      <div className="ct-toolbar">
        <h2 className="ct-toolbar-title">Subcontract requests</h2>
        <label>
          Show
          <select value={filter} onChange={(e) => setFilter(e.target.value as SubcontractRequestFilter)} aria-label="Which requests to show">
            {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </label>
      </div>
      {/* A list with nothing in it says so itself, in the words of the filter. */}
      <StateView query={view} isEmpty={() => false}>
        {(items) => {
          const shown = items.filter((r) => matchesRequest(r, search));
          return (
            <>
              <SubcontractRequestList
                title={current.title}
                titleId="ct-requests-queue"
                items={shown}
                showJob
                showRequester
                empty={items.length > 0 ? 'No requests match your search.' : current.empty}
              />
              {q.hasNextPage && (
                <div className="ct-actions">
                  <Button variant="secondary" onClick={() => void q.fetchNextPage()} isLoading={q.isFetchingNextPage} disabled={q.isFetchingNextPage}>
                    Show more
                  </Button>
                  <span className="ct-help">
                    {search.trim() !== ''
                      ? `The search covers the ${items.length} requests loaded so far. Show more to search the rest.`
                      : `Showing the oldest ${items.length}. There are more.`}
                  </span>
                </div>
              )}
            </>
          );
        }}
      </StateView>
    </>
  );
}
